import { NavigateFunction } from 'react-router-dom';
import { RoleType } from '../types';
import { featureAccess, LimitedFeatureType } from '../services/appAccess.service';
import { FeatureAccess } from '../pages/AccountSettings/components/rolesAndPermissions/PermissionsList';

export const ROUTES_MAP = {
  LOGIN: '/login',
};

export const REQUIRED_PROPERTIES = {
  AUTH: 'auth',
};

export const VIEWS_MAP = {
  DEFAULT: '/',
  LOGIN: 'login',
  TRANSACTIONS: 'transactions',
  DASHBOARD: 'dashboard',
  DIRECT_DEPOSIT: 'ach-transfer',
  CASH_DEPOSIT: 'cash-deposit',
  EXPENSES: 'expenses',
  STATEMENTS: 'statements',
  PROFIT_AND_LOSS: 'profit-and-loss',
  STATEMENT_OF_CASH_FLOWS: 'cash-flow',
  NOT_EXIST: '*',
  CATEGORIZE: 'categorize',
  LILI_CARD: 'my-lili-card',
  EMERGENCY_BUCKET_START: 'emergency-bucket-start',
  EMERGENCY_BUCKET_DASHBOARD: 'emergency-bucket-dashboard/:main',
  EMERGENCY_BUCKET_ADD_MONEY: 'emergency-bucket-add-money',
  EMERGENCY_BUCKET_WITHDRAW_MONEY: 'emergency-bucket-withdraw-money',
  EMERGENCY_BUCKET_TRANSACTIONS: 'emergency-bucket-transactions',
  SEND_MONEY: 'send-money',
  ADD_MONEY: 'add-money',
  INVOICES: 'invoices/:initialStep?',
  SERVICES_AND_PRODUCTS: 'services-and-products',
  CLIENTS: 'clients',
  CREATE_INVOICE: 'invoice/create',
  INSTRUCTIONS: 'instructions',
  TAX_BUCKET_DASHBOARD: 'tax-bucket-dashboard/:main',
  TAX_BUCKET_PERSONALIZATION: 'tax-bucket-personalization',
  TAX_BUCKET_ADD: 'tax-bucket-add-money',
  TAX_BUCKET_WITHDRAW: 'tax-bucket-withdraw-money',
  TAX_BUCKET_TRANSACTIONS: 'tax-bucket-transactions',
  CHECK_DEPOSIT: 'check-deposit',
  APP_CENTER: 'app-center',
  APP_CENTER_INSTRUCTIONS: 'app-center/instructions/:vendorId',
  VERSION: 'version',
  ACTIVATE_CARD: 'activate-card',
  ORDER_NEW_CARD: 'order-new-card',
  SCHEDULE_C_SET_UP: 'schedule-c',
  BALANCE_UP: 'balance-up',
  ACH_TRANSFER: 'ach-transfer',
  ACH_OUT_TRANSFER: 'ach-out-transfer',
  CARD_TRACKER: 'card-tracker',
  CHANGE_PIN: 'change-pin',
  WIRE_TRANSFER: 'wire-transfer',
  INTERNATIONAL_WIRE_TRANSFER: 'international-wire-transfer',
  ACCOUNT_LIMITS: 'account-limits',
  CREATE_SUPPLIER: 'create-supplier/:supplierId?',
  ACCOUNT_SETTINGS: 'settings',
  ROUTING_NUMBER_CHANGE: 'routing-number-change',
  CPA: 'talk-to-cpa',
  SUPPLIER: {
    BASE: 'supplier',
    MANAGE: {
      BASE: 'manage',
      PARAM: ':supplierId?',
    },
    PICKER: 'picker',
  },
  MAIL_A_CHECK: 'mail-a-check',
  SAVINGS_BUCKET: {
    BASE: 'savings-bucket',
  },
  BILLS: {
    BASE: 'bills',
    ADD_NEW_BILL: 'add',
    UPLOAD_BILL_FILE: 'upload',
    CREATE_BILL: {
      BASE: 'create',
      PARAM: '/:billId?',
    },
    PAY_BILL: {
      BASE: 'pay',
      PARAM: '/:billId?',
    },
    ASK_VENDOR: {
      BASE: 'ask-vendor',
      REVIEW: '/review',
    },
  },
  GLOBAL_LOADER: 'loading-details',
  TAX_FORM: 'tax-forms/:formType/:generateStep?',
  RECOVERY: 'recovery',
  ACCOUNTANT_AI: 'accountant-ai',
  ACCOUNTANT_ONBOARDING: {
    START: 'accountant-onboarding',
    PERSONAL_DETAILS: 'personal-details',
    VERIFY_PHONE: 'verify-phone',
    CREATE_PASSWORD: 'create-password',
    COMPLETE_OB: 'complete-onboarding',
    OB_FAILED: {
      BASE: 'onboarding-failed',
      PARAM: '/:case?',
    },
  },
  INVITE_ACCOUNTANT: {
    INVITE: 'invite',
    DETAILS: 'details',
    VERIFY_PHONE: 'verify-phone',
  },
  VENDOR_DETAILS_REQUEST: 'vendor-details-request',
  MOVE_MONEY: {
    BASE: 'move-money',
    PARAM: '/:moveType?',
  },
};

export const ERROR_STATUS_MAP = {
  TOKEN_EXPIRED: 401,
};

export const SMALL_VIEWPORT = {
  UNFOCUSED_AREA: 'bg-bw07 fixed top-0 left-0 h-screen w-screen bg-opacity-70 z-50',
  MESSAGE_AREA:
    'flex font-metric text-lg flex-col m-[1.875rem] min-w[28.875rem] max-w-[36.375rem] bg-bw00 min-h-[6.875rem] rounded-xl cursor-default',
  ICON: 'w-[0.563rem] absolute flex self-end mr-[1.222rem] mt-[1.216rem] cursor-pointer',
  HEADING_STYLE: 'font-semibold ml-[1.875rem] mt-[1.875rem]',
  HEADING: 'You\'re too close',
  TEXT_STYLE: 'font-normal ml-[1.875rem] text-bw07 pb-[1.875rem] mr-[1.875rem]',
  TEXT: 'For the best experience make your window bigger or full screen',
};

export const DATA_CHECK_TYPES = {
  EMERGENCY_BUCKET_DASHBOARD: 'EMERGENCY_BUCKET_DASHBOARD',
};

export const ROUTES_TO_AVOID_INIT = [VIEWS_MAP.ACCOUNTANT_ONBOARDING.START, VIEWS_MAP.VENDOR_DETAILS_REQUEST];

export const ALLOWED_VIEWS_BY_ROLE = {
  ACCOUNTANT: [
    'transactions',
    'invoices',
    'bills',
    'profit-and-loss',
    'cash-flow',
    'expenses',
    'schedule-c',
    'statements',
    'categorize',
    'loading-details',
    'tax-forms',
    VIEWS_MAP.ACCOUNT_SETTINGS,
  ],
};

export const DEFAULT_VIEWS_BY_ROLE = {
  [RoleType.ACCOUNTANT]: `/${VIEWS_MAP.TRANSACTIONS}`,
  [RoleType.ADMIN]: `/${VIEWS_MAP.DASHBOARD}`,
};

export const ROUTES_TO_ALLOW_MOBILE = [VIEWS_MAP.VENDOR_DETAILS_REQUEST];

export const navigateToContinueWhereYouLeft = (email: string) => {
  const isInteg = window.location.host.includes('integ') || window.location.host.includes('localhost');
  const isSandbox = window.location.host.includes('sandbox');

  if (isSandbox) {
    window.location.replace(`https://sandbox.lili.co/ob/continue-onboarding?email=${email}`);
  } else if (isInteg) {
    window.location.replace(`https://integ.lili.co/ob/continue-onboarding?email=${email}`);
  } else {
    window.location.replace(`https://prod.lili.co/ob/continue-onboarding?email=${email}`);
  }
};

export const navigateToRelevantAddMoney = (navigateFunc: NavigateFunction) => {
  const addMoneyLickedAccountPermission = featureAccess(null, LimitedFeatureType.AddMoneyACHLinkedAccount);
  const addMoneyDirectDepositPermission = featureAccess(null, LimitedFeatureType.AddMoneyACHDirectDeposit);
  if (addMoneyLickedAccountPermission === FeatureAccess.READ_WRITE) {
    navigateFunc(`/${VIEWS_MAP.ADD_MONEY}`);
  } else if (addMoneyDirectDepositPermission === FeatureAccess.READ_WRITE) {
    navigateFunc(`/${VIEWS_MAP.DIRECT_DEPOSIT}`);
  }
};
